/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@emotion/react";
import MUIDataTable from "mui-datatables";
import { Button, Chip, Grid, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import { getMuiTheme } from "../../Utils/globalStyleTables";
import { Vocabulary } from "../../Utils/Vocabulary";
import {
  PER_PAGE_DEFAULT,
  RolesEnum,
  tableLayoutOnMobile,
} from "../../Utils/Constants";
import { urlEnum } from "../../Utils/urlEnum";
import { getData } from "../../Services/getData";
import { updateData } from "../../Services/updateData";
import { isMobile } from "../../Utils/utils";
import GenericModal from "../../Modal/GenericModal";

type Organization = {
  id: number;
  organization: {
    id: any;
    name: string;
    cui: string;
    databaseName: string;
    isActive: boolean;
  };
  email: string;
  isActive: boolean;
};

type State = {
  organizations: Organization[];
  count: number;
};

type ConfirmState = {
  open: boolean;
  organizationId: number | null;
  activate: boolean;
};

export default function Applications() {
  const navigate = useNavigate();
  const location = useLocation();
  const roles = localStorage?.roles || "";
  const permissions = localStorage?.permissions || "";
  const canCreateApplication =
    roles.includes(RolesEnum.admin) ||
    permissions.includes("createNewApplication");

  const [filters, setFilters] = useState({
    page: 0,
    perPage: PER_PAGE_DEFAULT,
  });
  const [state, setState] = useState<State>({ organizations: [], count: 0 });
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    organizationId: null,
    activate: false,
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = parseInt(params.get("page") || "1", 10) - 1;
    const perPage = parseInt(
      params.get("perPage") || String(PER_PAGE_DEFAULT),
      10,
    );
    const newFilters = { page, perPage };
    setFilters(newFilters);
    fetchOrganizations(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  async function fetchOrganizations(currentFilters: {
    page: number;
    perPage: number;
  }) {
    const url = `${urlEnum.organizations}?page=${currentFilters.page}&per_page=${currentFilters.perPage}`;
    try {
      const response = await getData(url);
      const data = response.data;
      setState({
        organizations: data?.results || data || [],
        count: data?.nrResults ?? data?.data?.length ?? 0,
      });
    } catch (_error) {
      setState({ organizations: [], count: 0 });
    }
  }

  function openConfirm(organizationId: number, currentlyActive: boolean) {
    setConfirm({ open: true, organizationId, activate: !currentlyActive });
  }

  function closeConfirm() {
    setConfirm({ open: false, organizationId: null, activate: false });
  }

  async function toggleActiveStatus() {
    if (!confirm.organizationId) return;
    try {
      await updateData(
        `${urlEnum.organizations}/${confirm.organizationId}/toggleActive`,
        { is_active: confirm.activate },
      );
      await fetchOrganizations(filters);
    } finally {
      closeConfirm();
    }
  }

  const columns = [
    {
      label: Vocabulary.id,
      name: "id",
      options: { sort: false, display: false },
    },
    {
      label: Vocabulary.name,
      name: "organization",
      options: {
        sort: false,
        customBodyRender: (value: any) => (
          <Typography>{value?.name}</Typography>
        ),
      },
    },
    {
      label: Vocabulary.CUI,
      name: "organization",
      options: {
        sort: false,
        customBodyRender: (value: any) => <Typography>{value?.cui}</Typography>,
      },
    },
    {
      label: Vocabulary.email,
      name: "email",
      options: {
        sort: false,
        customBodyRender: (value: string) => <Typography>{value}</Typography>,
      },
    },
    {
      label: Vocabulary.status,
      name: "organization",
      options: {
        sort: false,
        customBodyRender: (value: Organization) => {
          const isActive = value?.isActive == null || value?.isActive === true;
          return (
            <Chip
              size="small"
              label={isActive ? Vocabulary.active : Vocabulary.inactive}
              style={{
                backgroundColor: isActive ? "#e8f5e9" : "#fce4ec",
                color: isActive ? "#2e7d32" : "#c62828",
                fontWeight: 600,
                fontSize: 11,
              }}
            />
          );
        },
      },
    },
    {
      label: Vocabulary.actions,
      name: "id",
      options: {
        sort: false,
        empty: true,
        customBodyRenderLite: (rowIndex: number) => {
          const org = state.organizations[rowIndex]?.organization;
          const isActive = org?.isActive == null || org?.isActive === true;
          const siteUrl = `https://${org?.databaseName}.erp.xauto.ro`;
          const id = org?.id;
          return (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                justifyContent: "flex-start",
                marginTop: -10,
                marginBottom: -10,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon fontSize="small" />}
                onClick={() =>
                  window.open(siteUrl, "_blank", "noopener,noreferrer")
                }
                style={{
                  borderColor: "#4D5E80",
                  color: "#4D5E80",
                  textTransform: "none",
                  borderRadius: 8,
                }}
              >
                {Vocabulary.accessSite}
              </Button>
              {isActive ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<BlockIcon fontSize="small" />}
                  onClick={() => openConfirm(id, true)}
                  style={{
                    borderColor: "#FF6633",
                    color: "#FF6633",
                    textTransform: "none",
                    borderRadius: 8,
                  }}
                >
                  {Vocabulary.deactivateApplication}
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                  onClick={() => openConfirm(id, false)}
                  style={{
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    textTransform: "none",
                    borderRadius: 8,
                  }}
                >
                  {Vocabulary.activateApplication}
                </Button>
              )}
            </div>
          );
        },
      },
    },
  ];

  return (
    <>
      <Grid
        container
        spacing={3}
        style={{
          padding: isMobile() ? 0 : 20,
          backgroundColor: "#f7f8fa",
          minHeight: "100vh",
        }}
      >
        <Grid item xs={12}>
          <ThemeProvider theme={getMuiTheme()}>
            <MUIDataTable
              title={null}
              data={state.organizations}
              columns={columns}
              options={{
                rowsPerPage: filters.perPage,
                page: filters.page,
                selectableRows: "none" as any,
                customToolbar: canCreateApplication
                  ? () => (
                      <Button
                        variant="contained"
                        onClick={() =>
                          window.open(
                            process.env.REACT_APP_REGISTER_URL,
                            "_blank",
                          )
                        }
                        style={{ backgroundColor: "#FF6633", color: "#fff" }}
                      >
                        {Vocabulary.createNewApplication}
                      </Button>
                    )
                  : undefined,
                responsive: tableLayoutOnMobile,
                filterType: "dropdown",
                filter: false,
                download: false,
                sort: false,
                serverSide: true,
                count: state.count,
                viewColumns: false,
                fixedHeader: false,
                fixedSelectColumn: false,
                rowsPerPageOptions: [
                  15,
                  50,
                  100,
                  state?.count > 100 ? state?.count : 1000,
                ],
                print: false,
                search: true,
                rowHover: false,
                textLabels: {
                  body: { noMatch: Vocabulary.noData },
                  viewColumns: { title: Vocabulary.viewColumns },
                  toolbar: {
                    downloadCsv: Vocabulary.downloadExcel,
                    viewColumns: Vocabulary.selectColumns,
                  },
                },
                downloadOptions: {
                  filterOptions: { useDisplayedColumnsOnly: true },
                },
                onChangePage: (page: number) => {
                  navigate(
                    `/applications?page=${page + 1}&perPage=${filters.perPage}`,
                  );
                  window.scrollTo(0, 0);
                },
                onChangeRowsPerPage: (numberOfRows: number) => {
                  navigate(`/applications?page=1&perPage=${numberOfRows}`);
                  window.scrollTo(0, 0);
                },
              }}
            />
          </ThemeProvider>
        </Grid>
      </Grid>

      <GenericModal
        open={confirm.open}
        onClose={closeConfirm}
        title={
          confirm.activate
            ? Vocabulary.activateApplication
            : Vocabulary.deactivateApplication
        }
      >
        <p style={{ textAlign: "center", fontSize: 20 }}>
          {confirm.activate
            ? Vocabulary.activateApplicationMessage
            : Vocabulary.deactivateApplicationMessage}
        </p>
        <Grid container spacing={isMobile() ? 1 : 4} style={{ padding: 10 }}>
          <Grid item md={6} xs={12}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              fullWidth
              onClick={closeConfirm}
            >
              {Vocabulary.no}
            </Button>
          </Grid>
          <Grid item md={6} xs={12}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={toggleActiveStatus}
              style={{ color: "white" }}
            >
              {Vocabulary.yes}
            </Button>
          </Grid>
        </Grid>
      </GenericModal>
    </>
  );
}
